/*global $*/
/*global toastr*/
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import assign from 'lodash/assign';
import find from 'lodash/find';
import map from 'lodash/map';
import compact from 'lodash/compact';
import ToggleSwitch from 'zignal-landscape/lib/ToggleSwitch';
import Checkbox from 'zignal-landscape/lib/Checkbox';
import Radio from 'zignal-landscape/lib/Radio';
import Select from 'zignal-landscape/lib/Select';
import Field from 'zignal-landscape/lib/Field';
import Label from 'zignal-landscape/lib/Label';
import Button from 'zignal-landscape/lib/Button';
import classNamesBind from 'classnames/bind';
import styles from './Schedule.css';

function paddedRange(start, end, step) {
  const retVal = [];
  for (let i = start; i <= end; i += step) {
    const str = String(i);
    const pad = '00';
    const label = pad.substring(0, pad.length - str.length) + str;
    retVal.push({value: i, label});
  }
  return retVal;
}

const classNames = classNamesBind.bind(styles);

const selectStyles = {
  singleValue: base => ({
    ...base,
    padding: 0,
    lineHeight: 'inherit'
  })
};

const DAYS_OF_WEEK_ITEMS = [
  {value: 'sun', label: 'S', fullText: 'Sunday'},
  {value: 'mon', label: 'M', fullText: 'Monday'},
  {value: 'tue', label: 'T', fullText: 'Tuesday'},
  {value: 'wed', label: 'W', fullText: 'Wednesday'},
  {value: 'thu', label: 'T', fullText: 'Thursday'},
  {value: 'fri', label: 'F', fullText: 'Friday'},
  {value: 'sat', label: 'S', fullText: 'Saturday'}
];

const DAYS_OF_MONTH_ITEMS = paddedRange(1, 28, 1); // Not supporting days 29 - 31 for month consistency
const HOUR_ITEMS = paddedRange(1, 12, 1);
const MINUTE_ITEMS = paddedRange(0, 55, 5);

const TIMEFRAME_MAP = {
  daily: 'days',
  weekly: 'weeks',
  monthly: 'months'
};

function formatTimezoneItem(item) {
  if (item.indexOf('/') > -1) {
    const tokens = item.split('/');
    return tokens.join('/').split('_').join(' ');
  }
  return item;
}

function getJobRecurrence(job) {
  // Since we only have one job recurrence per Report, always return the first recurrence.
  return job.local_recurrence[0];
}

function getJobScheduleText(job) {
  if (!job.active) {
    return 'This report is currently inactive.';
  }
  const jobRecurrence = getJobRecurrence(job);
  const jobMoment = moment({
    hour: jobRecurrence.hour,
    minute: jobRecurrence.minute
  });

  const time = jobMoment.format('h:mm a');

  if (job.repeat === 'daily') {
    return `This report is already scheduled to be sent every day at ${time}.`;
  }
  if (job.repeat === 'weekly') {
    const dayOfWeek = find(DAYS_OF_WEEK_ITEMS, {value: jobRecurrence.day}).fullText;
    return `This report is already scheduled to be sent every ${dayOfWeek} at ${time}.`;
  }
  if (job.repeat === 'monthly') {
    const dayOfMonth = moment({date: jobRecurrence.dayOfMonth}).format('Do');
    return `This report is already scheduled to be sent every month on the ${dayOfMonth} at ${time}.`;
  }

  return null;
}

function getStateScheduleText(state) {
  const time = `${state.hour}:${find(MINUTE_ITEMS, {value: state.minute}).label} ${state.ampm}`;

  if (state.repeat === 'daily') {
    return `This report will be sent every day at ${time} with data from the past 24 hours.`;
  }
  if (state.repeat === 'weekly') {
    const dayOfWeek = find(DAYS_OF_WEEK_ITEMS, {value: state.dayOfWeek}).fullText;
    return `This report will be sent every ${dayOfWeek} at ${time} with data from the past week.`;
  }
  if (state.repeat === 'monthly') {
    const dayOfMonth = moment({date: state.dayOfMonth}).format('Do');
    return `This report will be sent every month on the ${dayOfMonth} at ${time} with data from the past month.`;
  }
  return null;
}

class Schedule extends React.Component {
  constructor(props) {
    super(props);

    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleSubmitClick = this.handleSubmitClick.bind(this);

    const { job } = this.props;
    this.state = this.createStateFromJob(job);
  }

  getDefaultJob() {
    const { report, campaignId, profileId, user, users } = this.props;

    const recipients = find(users, {id: user.id}) ? [user.id] : [];
    const defaultJob = {
      active: false,
      repeat: 'daily',
      timezone: user.timezone,
      type: 'report',
      relation_type: 'campaign',
      relation_id: campaignId,
      owner: user.id,
      local_recurrence: [{
        day: 'mon',
        dayOfMonth: 1,
        hour: 7, // 24 hour format
        minute: 0,
        timeframe: 1,
        timeframe_unit: 'days'
      }],
      parameters: {
        file_type: 'pdf',
        users: recipients,
        campaign: profileId, // will become `Report.candidate`; we should have named this `parameters.profile`
        widgetAnnotationId: report.id
      }
    };
    return defaultJob;
  }

  createStateFromJob(jobOrEmpty) {
    const jobExists = (!! jobOrEmpty.created_at);
    const job = assign(this.getDefaultJob(), jobOrEmpty);
    const jobRecurrence = getJobRecurrence(job);
    const jobDayOfMonth = jobRecurrence.dayOfMonth;
    const jobDayOfWeek = jobRecurrence.day;
    const jobMoment = moment({hour: jobRecurrence.hour, minute: jobRecurrence.minute});
    const {
      campaign,
      users,
      file_type: fileType,
    } = job.parameters;

    return {
      job,
      jobExists,
      jobScheduleText: getJobScheduleText(job),
      active: job.active,
      repeat: job.repeat,
      timezone: job.timezone,
      hour: parseInt(jobMoment.format('h'), 10), // 12 hour format for UI state
      minute: parseInt(jobMoment.format('m'), 10),
      ampm: jobMoment.format('a'),
      dayOfWeek: jobDayOfWeek === '*' ? 'mon' : jobDayOfWeek,
      dayOfMonth: jobDayOfMonth === '*' ? 1 : jobDayOfMonth,
      profileId: campaign, // we should have named this `parameters.profile`
      recipients: users,
      pdfChecked: fileType === 'pdf' || fileType === 'both',
      pngChecked: fileType === 'png' || fileType === 'both',
      isModified: false
    };
  }

  handleInputChange(key, value) {
    const newState = {
      isModified: true
    };
    newState[key] = value;
    this.setState(newState);
  }

  handleSubmitClick() {
    const { profileId } = this.props;
    const {
      job,
      hour,
      minute,
      dayOfWeek,
      dayOfMonth,
      ampm,
      active,
      timezone,
      recipients,
      pdfChecked,
      pngChecked,
      repeat
    } = this.state;

    let fileType;
    if (pdfChecked && pngChecked) {
      fileType = 'both';
    } else if (pdfChecked) {
      fileType = 'pdf';
    } else {
      fileType = 'png';
    }

    let hour24 = hour;
    if (ampm === 'am' && hour === 12) {
      hour24 = 0;
    } else if (ampm === 'pm' && hour < 12) {
      hour24 = hour + 12;
    }

    const recurrence = {
      day: repeat === 'weekly' ? dayOfWeek : '*',
      dayOfMonth: repeat === 'monthly' ? dayOfMonth : '*',
      hour: hour24,
      minute,
      timeframe: 1,
      timeframeUnit: TIMEFRAME_MAP[repeat]
    };

    const updatedJobParameters = assign({}, job.parameters, {
      file_type: fileType,
      campaign: profileId, // we should have named this `parameters.profile`
      users: recipients,
    });

    const updatedJob = assign({}, job, {
      repeat,
      active,
      timezone,
      parameters: updatedJobParameters,
      recurrence
    });

    Vent.trigger('report:scheduled', { repeat, format:fileType });

    this.updateJob(updatedJob);
  }

  updateJob(job) {
    const self = this;
    const type = job.id ? 'PUT' : 'POST';
    const url = job.id ? `/api/jobs/${job.id}` : '/api/jobs';
    self.setState({ isSaving: true });
    // TODO: Replace with fetch polyfill once available
    $.ajax({
      url,
      dataType: 'json',
      data: JSON.stringify(job),
      contentType: 'application/json',
      type,
      processData: false,
      success: (result) => {
        self.setState({ isSaving: false });
        toastr.success('Success! Your report has been updated.');
        self.setState(self.createStateFromJob(result));
      },
      error: () => {
        self.setState({ isSaving: false });
        toastr.error('There was an error processing your request. Please try again.');
      }
    });
  }

  // eslint-disable-next-line complexity
  render() {
    const {
      users,
      user,
      timezones,
      candidateNames,
      profileId: selectedProfileId,
    } = this.props;

    const usersMap = users.map((u) => {
      return {
        value: u.id,
        label: `${u.first_name} ${u.last_name}`
      };
    });

    const {
      active,
      timezone,
      hour,
      ampm,
      minute,
      dayOfWeek,
      dayOfMonth,
      recipients,
      profileId: savedProfileId,
      pdfChecked,
      pngChecked,
      repeat,
      isModified,
      jobExists,
      jobScheduleText
    } = this.state;

    const timezoneSelectData = {
      placeholder: 'Select Time Zone',
      data: timezones.map((tz) => {
        return {
          value: tz,
          label: formatTimezoneItem(tz)
        };
      })
    };

    const selectedProfileName = candidateNames[selectedProfileId];
    const savedProfileName = (jobExists
      ? candidateNames[savedProfileId]
      : selectedProfileName // nothing saved yet = use whatever's selected
    );
    const isProfileChanged = (savedProfileName !== selectedProfileName);
    let profileNameSummary = null;
    if (isProfileChanged) {
      profileNameSummary = (
        <div className={classNames('subHeader', 'summary')}>
          If you would like your scheduled report to display data from
          the <b>{selectedProfileName}</b> Dataset,
          click "Update Report".
        </div>
      );
    }

    let isSubmitButtonEnabled = (
      (pdfChecked || pngChecked) &&
      (recipients.length > 0) &&
      (isModified || isProfileChanged)
    );
    if (repeat === 'weekly') {
      isSubmitButtonEnabled = isSubmitButtonEnabled && dayOfWeek !== '*';
    }
    if (repeat === 'monthly') {
      isSubmitButtonEnabled = isSubmitButtonEnabled && dayOfMonth !== '*';
    }

    const dayOfMonthPicker = (
      <div>
        <div className={styles.subHeader}>Day of Month</div>
        <Select
          value={find(DAYS_OF_MONTH_ITEMS, {value: dayOfMonth})}
          onChange={(item) => this.handleInputChange('dayOfMonth', parseInt(item.value, 10))}
          options={DAYS_OF_MONTH_ITEMS}
          selectStyles={selectStyles}
        />
        <div className={styles.subText}>Days 29-31 are disabled due to monthly inconsistency</div>
      </div>
    );

    const daysOfWeek = DAYS_OF_WEEK_ITEMS.map((dayInfo) => (
      <span key={dayInfo.value}>
        <Radio
          id={`dayOfWeek${dayInfo.value}`}
          name="dayOfWeek"
          checked={dayOfWeek === dayInfo.value}
          onChange={() => this.handleInputChange('dayOfWeek', dayInfo.value)}
        />
        <Label htmlFor={`dayOfWeek${dayInfo.value}`} inline>{dayInfo.label}</Label>
      </span>
    ));

    const dayOfWeekPicker = (
      <div className={styles.dayOfWeek}>
        <div className={styles.subHeader}>Day of Week</div>
        {daysOfWeek}
      </div>
    );

    const ampmItems = [{value: 'am', label: 'AM'}, {value: 'pm', label: 'PM'}];
    const timePicker = (
      <div className={styles.subSection}>
        <Field>
          {repeat === 'weekly' ? dayOfWeekPicker : null}
          {repeat === 'monthly' ? dayOfMonthPicker : null}
          <div className={styles.subHeader}>Time</div>
          <div>
            <Select
              inline
              isSearchable={false}
              className={styles.inlineSelect}
              value={find(HOUR_ITEMS, {value: hour})}
              onChange={(item) => this.handleInputChange('hour', item.value)}
              options={HOUR_ITEMS}
              selectStyles={selectStyles}
            />
            <span className={styles.colon}>:</span>
            <Select
              inline
              isSearchable={false}
              className={styles.inlineSelect}
              value={find(MINUTE_ITEMS, {value: minute})}
              onChange={(item) => this.handleInputChange('minute', item.value)}
              options={MINUTE_ITEMS}
              selectStyles={selectStyles}
            />
            <span className={styles.colon} />
            <Select
              inline
              isSearchable={false}
              className={styles.inlineSelect}
              value={find(ampmItems, {value: ampm})}
              onChange={(item) => this.handleInputChange('ampm', item.value)}
              options={ampmItems}
              selectStyles={selectStyles}
            />
          </div>
          <div className={styles.summary}>{getStateScheduleText(this.state)}</div>
        </Field>
      </div>
    );

    const timezoneSelect = (
      <Select
        placeholder={timezoneSelectData.placeholder}
        searchable
        options={timezoneSelectData.data}
        value={find(timezoneSelectData.data, {value: timezone})}
        onChange={(item) => this.handleInputChange('timezone', item.value)}
        clearable={false}
        selectStyles={selectStyles}
      />
    );

    const recipientsSelect = (
      <Field>
        <Label>Recipients</Label>
        <Select
          options={usersMap}
          isMulti
          delimiter={'|'}
          value={recipients.map((recipient) => { return find(usersMap, {value: recipient});})}
          onChange={(item) => this.handleInputChange('recipients', compact(map(item, 'value')))}
        />
      </Field>
    );

    const repeatItems = [
      {value: 'daily', label: 'Daily'},
      {value: 'weekly', label: 'Weekly'},
      {value: 'monthly', label: 'Monthly'}
    ];

    return (
      <div className={styles.container}>
        <div className={styles.form}>
          <Field>
            {jobScheduleText}
          </Field>
          <Field>
            <Label>Active</Label>
            <ToggleSwitch
              id="ToggleSwitch_active"
              enabled={active}
              onChange={(e) => this.handleInputChange('active', e.target.checked)}
            />
          </Field>
          <Field>
            <Label>Dataset</Label>
            <div className={styles.text}>{savedProfileName}</div>
            {profileNameSummary}
          </Field>
          <Field>
            <Label>Repeat</Label>
            <Select
              options={repeatItems}
              isClearable={false}
              isSearchable={false}
              placeholder="Select recurrence"
              value={find(repeatItems, {value: repeat})}
              onChange={(item) => this.handleInputChange('repeat', item.value)}
              selectStyles={selectStyles}
            />
          </Field>
          {timePicker}
          <Field>
            <Label>Timezone</Label>
            {timezones.length > 0 ? timezoneSelect : null}
          </Field>
          {user.roles.indexOf('ROLE_CAMPAIGN_ADMIN') === -1 ? null :  recipientsSelect}
          <Field>
            <Label>Delivery Format</Label>
            <Checkbox
              id="formatSchedulePDF"
              name="PDF"
              checked={pdfChecked}
              onChange={(e) => this.handleInputChange('pdfChecked', e.target.checked)}
            />
            <Label htmlFor="formatSchedulePDF" inline>PDF</Label>
            <Checkbox
              id="formatSchedulePNG"
              name="PNG"
              checked={pngChecked}
              onChange={(e) => this.handleInputChange('pngChecked', e.target.checked)}
            />
            <Label htmlFor="formatSchedulePNG" inline>PNG</Label>
          </Field>
        </div>
        <div className={styles.submitButton}>
          <Button
            onClick={this.handleSubmitClick}
            buttonStyle="positive"
            disabled={!isSubmitButtonEnabled || this.state.isSaving}
          >
            {jobExists ? 'Update Report' : 'Schedule Report'}
          </Button>
        </div>
      </div>
    );
  }
}

Schedule.propTypes = {
  campaignId: PropTypes.string.isRequired,
  profileId: PropTypes.string.isRequired,
  candidateNames: PropTypes.object,
  report: PropTypes.object.isRequired,
  user: PropTypes.object,
  users: PropTypes.array,
  job: PropTypes.object,
  onSubmit: PropTypes.func,
  timezones: PropTypes.array
};

Schedule.defaultProps = {
  users: [],
  job: {},
  timezones: [],
  onSubmit: () => { /* noop */ }
};

export default Schedule;
